(() => {
  const plain = (v = '') => String(v).trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[’']/g, ' ').replace(/[^a-z0-9./+ -]+/g, ' ').replace(/\s+/g, ' ').trim();
  const norm = (v = '') => String(v).replace(/[₂²]/g, '2').replace(/[₀⁰]/g, '0').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const split = (v = '') => String(v).split(',').map(norm).filter(Boolean);
  const VERIFIED_STATUSES = new Set(['verifie', 'verifiee', 'valide', 'validee', 'verified', 'confirmed', 'controle', 'controlee']);
  const STRICT_TYPE_CATEGORIES = new Set(['magazines', 'upgrade', 'hpa', 'power']);
  const families = (value = '') => {
    const text = plain(value);
    const out = new Set();
    if (/\baeg\b|\baep\b|electrique|\bngrs\b|next gen|recoil shock|\bebbr?\b/.test(text)) out.add('aeg');
    if (/\bgbbr?\b|\bmws\b|\bgaz\b|\bgas\b|\bco2\b|\bnbb\b/.test(text)) out.add('gas');
    if (/\bhpa\b/.test(text)) out.add('hpa');
    if (/\bspring\b|ressort/.test(text)) out.add('spring');
    return out;
  };
  const disjoint = (a, b) => a.size && b.size && ![...a].some((item) => b.has(item));
  const generations = (value = '') => {
    const text = plain(value);
    const out = new Set();
    for (const match of text.matchAll(/\bgen(?:eration)?\s*([1-9])\s*(?:a|to|-)\s*([1-9])\b/g)) {
      const start = Number(match[1]);
      const end = Number(match[2]);
      for (let i = Math.min(start, end); i <= Math.max(start, end); i += 1) out.add(i);
    }
    for (const match of text.matchAll(/\bgen(?:eration)?\s*([1-9])\b/g)) out.add(Number(match[1]));
    return out;
  };
  const glockModels = (value = '') => {
    const text = plain(value);
    const out = new Set();
    ['17','18','19','20','21','22','23','24','25','26','27','28','29','30','31','32','33','34','35','36','37','38','42','43','44','45','46','47','48','49'].forEach((model) => {
      const re = new RegExp(`\\b(?:glock\\s*)?g?${model}\\b`);
      if (re.test(text)) out.add(model);
    });
    return out;
  };
  const overlapOrUnknown = (a, b) => !a.size || !b.size || [...a].some((item) => b.has(item));
  const negative = (value = '') => /non couvert|non destine|non compatible|incompatible|exclu|ne pas|pas automatiquement|ne devient pas compatible|ne valide pas/.test(plain(value));

  class CompatibleProducts {
    constructor(root) {
      if (root.dataset.awReady === 'true') return;
      this.root = root;
      this.grid = root.querySelector('[data-aw-products-grid]');
      this.loading = root.querySelector('[data-aw-loading]');
      this.empty = root.querySelector('[data-aw-empty]');
      this.toolbar = root.querySelector('[data-aw-toolbar]');
      this.notice = root.querySelector('[data-aw-notice]');
      this.count = root.querySelector('[data-aw-selected-count]');
      this.addSelected = root.querySelector('[data-aw-add-selected]');
      this.summary = root.querySelector('[data-aw-platform-summary]');
      this.summaryLabel = root.querySelector('[data-aw-platform-label]');
      this.limit = Number(root.dataset.maxResults || 12);
      this.endpointUrl = root.dataset.endpointUrl || '/search';
      this.endpointSection = root.dataset.endpointSection || 'aw-compatible-products-endpoint';
      this.currentProductId = String(root.dataset.currentProductId || '');
      this.currentProductTitle = String(root.dataset.currentProductTitle || '');
      this.readyToPlay = root.dataset.awPackMode === 'ready-to-play';
      this.currentTypes = split(root.dataset.currentTypes || '');
      this.cards = [];
      this.seen = new Set();
      this.loadedPlatform = '';
      this.busy = false;
      this.controller = null;
      this.safetyRenderQueued = false;
      this.config = this.readConfig();
      this.bind();
      if (!this.readyToPlay) this.syncControls();
      this.render();
      root.dataset.awReady = 'true';
      this.safetyObserver = new MutationObserver((mutations) => {
        if (!mutations.some((mutation) => mutation.type === 'attributes' && mutation.attributeName === 'data-aw-technical-conflict')) return;
        if (this.safetyRenderQueued) return;
        this.safetyRenderQueued = true;
        requestAnimationFrame(() => {
          this.safetyRenderQueued = false;
          this.render();
        });
      });
      this.safetyObserver.observe(root, { subtree: true, attributes: true, attributeFilter: ['data-aw-technical-conflict'] });
      this.ensureResults();
    }

    read(key) { try { return localStorage.getItem(key) || ''; } catch (_) { return ''; } }
    write(key, value) { try { value ? localStorage.setItem(key, value) : localStorage.removeItem(key); } catch (_) {} }
    readyTypeLabel() { return this.currentTypes.join(', '); }
    configuredTypes() { return this.readyToPlay ? this.currentTypes : split(this.config.type || ''); }
    currentTypeFamilies() { return families(this.configuredTypes().join(' ')); }
    readConfig() {
      const defaultPlatform = String(this.root.dataset.defaultPlatform || '');
      return {
        platform: this.readyToPlay && defaultPlatform ? defaultPlatform : (this.read(this.root.dataset.storageKey || 'aw_selected_platform') || defaultPlatform),
        manufacturer: this.readyToPlay ? '' : this.read(this.root.dataset.manufacturerKey || 'aw_selected_manufacturer'),
        type: this.readyToPlay ? this.readyTypeLabel() : this.read(this.root.dataset.typeKey || 'aw_selected_replica_type')
      };
    }
    syncControls() {
      if (this.readyToPlay) return;
      const source = document.querySelector('[data-aw-compatibility]');
      if (!source) return;
      const platform = source.querySelector('[data-aw-platform-select]')?.value || '';
      const manufacturer = source.querySelector('[data-aw-manufacturer-select]')?.value || '';
      const type = source.querySelector('[data-aw-replica-type-select]')?.value || '';
      if (!platform && !manufacturer && !type) return;
      this.config = { platform, manufacturer, type };
      this.write(this.root.dataset.storageKey, platform);
      this.write(this.root.dataset.manufacturerKey, manufacturer);
      this.write(this.root.dataset.typeKey, type);
    }
    clearCards() {
      this.cards.forEach((card) => card.remove());
      this.cards = [];
      this.seen.clear();
      this.updateSelection();
    }
    collect(scope) {
      scope.querySelectorAll('[data-aw-product-card]').forEach((card) => {
        const id = String(card.dataset.productId || '');
        if (!id || id === this.currentProductId || this.seen.has(id)) return;
        this.seen.add(id);
        card.hidden = true;
        this.cards.push(card);
        this.grid?.appendChild(card);
      });
    }
    hasPlatform() { return Boolean(norm(this.config.platform)); }
    platformHandle() { return norm(this.config.platform); }
    candidateTitle(card) { return String(card?.querySelector('.aw-compatible-card__title')?.textContent || ''); }
    coreConflict(card) {
      if (!card) return true;
      if (card.dataset.awRelationBlocked === 'true') return true;
      if (card.dataset.awHasRelations === 'true' && card.dataset.awRelationFound !== 'true') return true;

      const candidateTitle = this.candidateTitle(card);
      const candidateText = `${candidateTitle} ${card.dataset.types || ''} ${card.dataset.awRelationCondition || ''}`;
      const currentText = `${this.currentProductTitle} ${this.configuredTypes().join(' ')}`;
      const category = card.dataset.awMerchCategory || 'other';
      const currentFamilies = this.currentTypeFamilies();
      const candidateFamilies = families(candidateText);

      if (STRICT_TYPE_CATEGORIES.has(category) && disjoint(currentFamilies, candidateFamilies)) return true;

      const currentGen = generations(currentText);
      const candidateGen = generations(candidateText);
      if (!overlapOrUnknown(currentGen, candidateGen)) return true;

      if (this.platformHandle() === 'glock') {
        const currentModels = glockModels(this.currentProductTitle);
        const candidateModels = glockModels(candidateText);
        if (!overlapOrUnknown(currentModels, candidateModels)) return true;
      }

      const condition = plain(card.dataset.awRelationCondition || '');
      if (/\bx300u?\b/.test(condition) && !/\bx300u?\b/.test(plain(this.currentProductTitle))) return true;

      const motif = plain(card.dataset.awRelationMotif || '');
      const currentPlain = plain(this.currentProductTitle);
      if (motif && negative(motif)) {
        if (/\bdor\b/.test(motif) && /\bdor\b/.test(currentPlain)) return true;
        if (/\bmws\b/.test(motif) && /\bmws\b/.test(currentPlain)) return true;
        if (/\bgbbr\b/.test(motif) && /\bgbbr\b/.test(currentPlain)) return true;
        if (/\bhpa\b/.test(motif) && /\bhpa\b/.test(currentPlain)) return true;
        if (/\baeg\b/.test(motif) && /\baeg\b/.test(currentPlain)) return true;
        if (/\bco2\b/.test(motif) && /\bco2\b/.test(currentPlain)) return true;
      }
      return false;
    }
    score(card) {
      if (this.coreConflict(card)) return -1;
      const platform = this.platformHandle();
      if (!platform || !split(card.dataset.platforms).includes(platform)) return -1;
      const manufacturers = split(card.dataset.manufacturers);
      const types = split(card.dataset.types);
      const manufacturer = norm(this.config.manufacturer);
      const configuredTypes = this.configuredTypes();
      let score = 100;
      if (manufacturer) score += manufacturers.includes(manufacturer) ? 30 : manufacturers.length ? -35 : -20;
      if (configuredTypes.length && types.length) score += configuredTypes.some((type) => types.includes(type)) ? 25 : -35;
      return score;
    }
    verification(card) {
      const status = norm(card?.dataset.verificationStatus || '');
      const confidence = Number(card?.dataset.confidenceScore || '');
      const date = String(card?.dataset.verificationDate || '').trim();
      return {
        complete: VERIFIED_STATUSES.has(status) && Number.isFinite(confidence) && confidence >= 90 && Boolean(date),
        confidence,
        date
      };
    }
    isEligible(card, score = this.score(card)) {
      if (this.coreConflict(card) || card?.dataset.awTechnicalConflict === 'true' || card?.dataset.awExactModelUnproven === 'true') return false;
      const platform = this.platformHandle();
      if (!platform || !split(card?.dataset.platforms).includes(platform)) return false;
      return score >= 100 && this.verification(card).complete;
    }
    badge(score, card) {
      if (this.coreConflict(card) || card?.dataset.awTechnicalConflict === 'true') return 'Incompatible avec la configuration sélectionnée';
      if (card?.dataset.awExactModelUnproven === 'true') return 'À vérifier — preuve modèle exact absente';
      if (!this.verification(card).complete) return 'Correspondance AW — à vérifier';
      if (this.isEligible(card, score)) return 'Données AW vérifiées';
      return 'Compatibilité à vérifier';
    }
    async loadPlatform(handle) {
      if (!handle) return;
      this.controller?.abort();
      this.controller = new AbortController();
      this.busy = true;
      this.loadedPlatform = '';
      this.clearCards();
      this.render();
      try {
        const target = new URL(this.endpointUrl, location.origin);
        target.searchParams.set('section_id', this.endpointSection);
        target.searchParams.set('q', handle);
        const response = await fetch(target, { headers: { Accept: 'text/html' }, signal: this.controller.signal });
        if (!response.ok) throw new Error('Chargement du pack Compatibility impossible.');
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const endpoint = doc.querySelector('[data-aw-compatible-endpoint]');
        if (!endpoint || norm(endpoint.dataset.platformHandle) !== handle) throw new Error('Index Compatibility indisponible.');
        if (handle !== this.platformHandle()) return;
        this.collect(endpoint);
        this.loadedPlatform = handle;
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[AW Compatibility index]', error);
        if (handle === this.platformHandle()) {
          this.loadedPlatform = handle;
          this.show('Le pack Compatibility est momentanément indisponible. Vérifiez la fiche du produit avant l’achat.', false);
        }
      } finally {
        if (handle === this.platformHandle()) {
          this.busy = false;
          this.controller = null;
          this.render();
        }
      }
    }
    ensureResults() {
      const handle = this.platformHandle();
      if (!handle) {
        this.controller?.abort();
        this.controller = null;
        this.busy = false;
        this.loadedPlatform = '';
        this.clearCards();
        this.render();
        return Promise.resolve();
      }
      if (this.loadedPlatform === handle) {
        this.render();
        return Promise.resolve();
      }
      return this.loadPlatform(handle);
    }
    render() {
      const handle = this.platformHandle();
      const active = Boolean(handle);
      const ready = active && this.loadedPlatform === handle;
      const ranked = ready ? this.cards
        .map((card) => {
          const conflict = this.coreConflict(card);
          card.dataset.awTechnicalConflict = conflict ? 'true' : 'false';
          return { card, score: conflict ? -1 : this.score(card) };
        })
        .filter((item) => item.score >= 0 && item.card.dataset.awTechnicalConflict !== 'true')
        .sort((a, b) => b.score - a.score) : [];
      this.cards.forEach((card) => { card.hidden = true; });
      const visibleItems = ranked.slice(0, this.limit);
      let eligibleCount = 0;
      visibleItems.forEach(({ card, score }) => {
        card.hidden = false;
        card.style.order = String(-score);
        const eligible = this.isEligible(card, score);
        card.dataset.awEligible = eligible ? 'true' : 'false';
        if (eligible) eligibleCount += 1;
        const badge = card.querySelector('[data-aw-card-badge]');
        if (badge) badge.textContent = this.badge(score, card);
        const checkbox = card.querySelector('[data-aw-product-checkbox]');
        if (checkbox) {
          checkbox.disabled = !eligible;
          if (!eligible) checkbox.checked = false;
          const selector = checkbox.closest('.aw-compatible-card__selector');
          if (selector) selector.hidden = !eligible;
        }
        const quick = card.querySelector('[data-aw-quick-add]');
        if (quick) {
          quick.disabled = !eligible;
          quick.hidden = !eligible;
        }
      });
      const visible = visibleItems.length;
      if (this.loading) {
        this.loading.hidden = !active || (ready && !this.busy);
        this.loading.textContent = this.busy ? 'Chargement du pack Compatibility…' : 'Sélectionnez une plateforme dans Compatibilité produit AW pour lancer la recherche.';
      }
      if (this.empty) this.empty.hidden = !(active && ready && !this.busy && visible === 0);
      if (this.grid) this.grid.hidden = !ready || visible === 0;
      if (this.toolbar) this.toolbar.hidden = !ready || visible === 0 || eligibleCount === 0;
      if (this.summary && this.summaryLabel) {
        this.summary.hidden = !active;
        this.summaryLabel.textContent = [this.config.platform, this.config.manufacturer, this.readyToPlay ? this.readyTypeLabel() : this.config.type].filter(Boolean).join(' · ');
      }
      this.updateSelection();
    }
    apply(detail = {}) {
      if (this.readyToPlay) {
        const defaultPlatform = String(this.root.dataset.defaultPlatform || '');
        const nextPlatform = norm(defaultPlatform);
        if (this.platformHandle() !== nextPlatform) this.loadedPlatform = '';
        this.config = { platform: defaultPlatform, manufacturer: '', type: this.readyTypeLabel() };
        this.render();
        this.ensureResults();
        return;
      }
      const previousPlatform = this.platformHandle();
      if (detail.platform !== undefined) this.write(this.root.dataset.storageKey, detail.platform || '');
      if (detail.manufacturer !== undefined) this.write(this.root.dataset.manufacturerKey, detail.manufacturer || '');
      if (detail.replicaType !== undefined) this.write(this.root.dataset.typeKey, detail.replicaType || '');
      this.config = {
        platform: detail.platform !== undefined ? detail.platform || '' : this.read(this.root.dataset.storageKey),
        manufacturer: detail.manufacturer !== undefined ? detail.manufacturer || '' : this.read(this.root.dataset.manufacturerKey),
        type: detail.replicaType !== undefined ? detail.replicaType || '' : this.read(this.root.dataset.typeKey)
      };
      const nextPlatform = this.platformHandle();
      if (nextPlatform !== previousPlatform) this.loadedPlatform = '';
      this.render();
      this.ensureResults();
    }
    bind() {
      this.root.addEventListener('change', (event) => {
        if (event.target.matches('[data-aw-product-checkbox]')) this.updateSelection();
      });
      this.root.addEventListener('click', (event) => {
        const quick = event.target.closest('[data-aw-quick-add]');
        if (quick) {
          event.preventDefault();
          const card = quick.closest('[data-aw-product-card]');
          if (quick.disabled || card?.dataset.awEligible !== 'true' || this.coreConflict(card)) return;
          this.add([{ id: Number(quick.dataset.variantId), quantity: 1, properties: this.properties() }], quick);
          return;
        }
        if (event.target.closest('[data-aw-add-selected]')) {
          const items = this.selectedItems();
          if (items.length) this.add(items, this.addSelected);
          return;
        }
        if (event.target.closest('[data-aw-select-all]')) {
          this.visibleBoxes().forEach((box) => { box.checked = true; });
          this.updateSelection();
          return;
        }
        if (event.target.closest('[data-aw-clear-selection]')) {
          this.visibleBoxes().forEach((box) => { box.checked = false; });
          this.updateSelection();
          return;
        }
        if (event.target.closest('[data-aw-reset-platform]')) {
          if (this.readyToPlay) return;
          [this.root.dataset.storageKey, this.root.dataset.manufacturerKey, this.root.dataset.typeKey].forEach((key) => this.write(key, ''));
          this.config = { platform: '', manufacturer: '', type: '' };
          this.loadedPlatform = '';
          this.ensureResults();
          dispatchEvent(new CustomEvent('aw:platform-reset'));
        }
      });
      const sync = (event) => this.apply(event.detail || {});
      addEventListener('aw:platform-changed', sync);
      addEventListener('aw:compatibility-changed', sync);
      addEventListener('storage', () => {
        if (this.readyToPlay) return;
        this.config = this.readConfig();
        this.loadedPlatform = '';
        this.ensureResults();
      });
    }
    properties() {
      const props = {};
      if (this.config.platform) props['Plateforme AW'] = this.config.platform;
      if (this.config.manufacturer) props['Fabricant AW'] = this.config.manufacturer;
      const typeLabel = this.readyToPlay ? this.readyTypeLabel() : this.config.type;
      if (typeLabel) props['Type de réplique AW'] = typeLabel;
      return props;
    }
    visibleBoxes() { return this.cards.filter((card) => !card.hidden).map((card) => card.querySelector('[data-aw-product-checkbox]')).filter((box) => box && !box.disabled && !this.coreConflict(box.closest('[data-aw-product-card]'))); }
    selectedItems() {
      const properties = this.properties();
      return this.visibleBoxes().filter((box) => box.checked).map((box) => ({ id: Number(box.value), quantity: 1, properties }));
    }
    updateSelection() {
      const count = this.selectedItems().length;
      if (this.count) this.count.textContent = count;
      if (this.addSelected) this.addSelected.disabled = count === 0;
    }
    async add(items, button) {
      const label = button?.textContent;
      if (button) { button.disabled = true; button.textContent = 'Ajout en cours…'; }
      try {
        const response = await fetch(this.root.dataset.cartUrl || '/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Ajout impossible.');
        this.show(`${items.length} produit(s) ajouté(s) au panier.`, true);
        this.visibleBoxes().forEach((box) => { box.checked = false; });
        this.updateSelection();
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: data }));
      } catch (error) {
        this.show(error.message || 'Une erreur est survenue.', false);
      } finally {
        if (button) {
          button.textContent = label;
          if (button === this.addSelected) this.updateSelection();
          else {
            const card = button.closest('[data-aw-product-card]');
            button.disabled = !this.isEligible(card) || this.coreConflict(card);
          }
        }
      }
    }
    show(message, success) {
      if (!this.notice) return;
      this.notice.hidden = !message;
      this.notice.textContent = message;
      this.notice.classList.toggle('is-success', success);
      this.notice.classList.toggle('is-error', !success);
    }
  }

  const init = (scope = document) => scope.querySelectorAll('[data-aw-compatible-products]').forEach((root) => {
    if (!root.awCatalogCompatibility) root.awCatalogCompatibility = new CompatibleProducts(root);
  });
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
